import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from 'react'
import { useParams } from 'react-router-dom'
import { getPortalConfig } from '@/api/portal'
import type { PortalConfigResponse } from '@/api/types'

/* ==========================================================================
   Portal Context
   Task F19: Updated to extract slug from URL path parameter
   ========================================================================== */

interface PortalContextValue {
  /** The pharmacy portal slug (e.g., "acme-pharmacy") */
  slug: string | null
  /** The full portal configuration (includes pharmacy id, branding, etc.) */
  config: PortalConfigResponse | null
  /** Whether the portal config is being loaded */
  loading: boolean
  /** Error if config could not be loaded */
  error: string | null
}

const PortalContext = createContext<PortalContextValue | null>(null)

export interface PortalProviderProps {
  children: ReactNode
  /** Override slug for testing or static configuration */
  defaultSlug?: string
}

/**
 * Portal provider that manages the pharmacy portal slug and configuration.
 * The slug is extracted from the URL path parameter (/:slug/*).
 * The config is fetched from the API to get pharmacy details.
 *
 * @example
 * ```tsx
 * <PortalProvider>
 *   <App />
 * </PortalProvider>
 * ```
 */
export function PortalProvider({ children, defaultSlug }: PortalProviderProps) {
  // Get slug from URL params (set by React Router)
  const { slug: urlSlug } = useParams<{ slug: string }>()
  const slug = defaultSlug || urlSlug || null

  // State for portal config
  const [config, setConfig] = useState<PortalConfigResponse | null>(null)
  const [loading, setLoading] = useState(!!slug)
  const [error, setError] = useState<string | null>(null)

  // Fetch portal config when slug changes
  useEffect(() => {
    if (!slug) {
      setConfig(null)
      setLoading(false)
      setError(null)
      return
    }

    // Capture slug in a const for the async function (TypeScript narrowing)
    const currentSlug = slug
    let cancelled = false

    async function fetchConfig() {
      setLoading(true)
      setError(null)

      try {
        const portalConfig = await getPortalConfig(currentSlug)
        if (!cancelled) {
          setConfig(portalConfig)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setConfig(null)
          setError(err instanceof Error ? err.message : 'Failed to load portal')
          setLoading(false)
        }
      }
    }

    fetchConfig()

    return () => {
      cancelled = true
    }
  }, [slug])

  // Compute the context value
  const value = useMemo(
    (): PortalContextValue => ({
      slug,
      config,
      loading,
      error,
    }),
    [slug, config, loading, error]
  )

  return (
    <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
  )
}

/**
 * Hook to access the portal slug from any component.
 * Must be used within a PortalProvider.
 *
 * @example
 * ```tsx
 * function ProductList() {
 *   const { slug } = usePortal()
 *
 *   useEffect(() => {
 *     if (slug) {
 *       fetchProducts(slug)
 *     }
 *   }, [slug])
 *
 *   return <div>...</div>
 * }
 * ```
 */
export function usePortal(): PortalContextValue {
  const context = useContext(PortalContext)
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider')
  }
  return context
}

/**
 * Convenience hook that returns just the slug.
 * Throws if slug is not available.
 * Note: This returns the slug immediately (from URL params) without waiting
 * for the portal config to load.
 *
 * @example
 * ```tsx
 * function ApiComponent() {
 *   const slug = usePortalSlug()
 *   // slug is guaranteed to be a string here
 *   return <div>Portal: {slug}</div>
 * }
 * ```
 */
export function usePortalSlug(): string {
  const { slug } = usePortal()

  if (!slug) {
    throw new Error('Portal slug is not available')
  }

  return slug
}

/**
 * Hook to get slug directly from URL params.
 * Use this when you need the slug but aren't inside PortalProvider,
 * or for navigation purposes.
 */
export function useSlugParam(): string | undefined {
  const { slug } = useParams<{ slug: string }>()
  return slug
}

export default PortalProvider
